import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import {
  Plus, Check, Leaf, FileText, FileDown, Share2, MessageCircle,
  Loader2, Users, Sparkles, Copy, Send, UtensilsCrossed, List, Trash2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import DishImage from "@/components/DishImage";
import PlateComposer from "@/components/PlateComposer";
import EnquiryForm from "@/components/EnquiryForm";
import { useLang } from "@/context/LanguageContext";

const DIETARY = [
  { key: "veg", label: "Veg" },
  { key: "jain", label: "Jain" },
];

export default function MenuBuilder() {
  const [dishes, setDishes] = useState([]);
  const [presets, setPresets] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [occasion, setOccasion] = useState("Wedding");
  const [guests, setGuests] = useState(50);
  const [selected, setSelected] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [diet, setDiet] = useState([]);

  const [shareId, setShareId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [view, setView] = useState("list");
  const [center, setCenter] = useState("rice");
  const { lang } = useLang();

  useEffect(() => {
    Promise.all([api.get("/dishes"), api.get("/presets"), api.get("/settings")])
      .then(([d, p, s]) => {
        setDishes(d.data);
        setPresets(p.data);
        setSettings(s.data);
        setActiveCat(s.data.categories?.[0] || "");
      })
      .catch(() => toast.error("Failed to load menu. Please retry."))
      .finally(() => setLoading(false));
  }, []);

  const categories = settings?.categories || [];
  const limits = settings?.category_limits || {};

  // reset share id whenever selection meaningfully changes
  useEffect(() => { setShareId(null); }, [selected, guests, occasion]);

  const countInCat = (cat) => selected.filter((s) => s.category === cat).length;

  const toggleDish = (dish) => {
    const exists = selected.find((s) => s.id === dish.id);
    if (exists) {
      setSelected(selected.filter((s) => s.id !== dish.id));
      return;
    }
    const limit = limits[dish.category];
    if (limit && countInCat(dish.category) >= limit) {
      toast.error(`You can pick up to ${limit} from ${dish.category}`);
      return;
    }
    setSelected([...selected, { id: dish.id, name: dish.name, category: dish.category, price: dish.price, image: dish.image, desc_mr: dish.desc_mr }]);
    toast.success(`${dish.name} added to your plate`);
  };

  const removeDish = (id) => setSelected(selected.filter((s) => s.id !== id));

  const clearAll = () => {
    setSelected([]);
    setShareId(null);
    toast.success(lang === "mr" ? "ताट रिकामं केलं" : "Cleared");
  };

  const applyPreset = (preset) => {
    const chosen = preset.dish_ids
      .map((id) => dishes.find((d) => d.id === id))
      .filter(Boolean)
      .map((d) => ({ id: d.id, name: d.name, category: d.category, price: d.price, image: d.image, desc_mr: d.desc_mr }));
    setSelected(chosen);
    setOccasion(preset.occasion);
    toast.success(`Loaded "${preset.name}" — customise it as you like`);
  };

  const perPlate = useMemo(() => selected.reduce((sum, d) => sum + (d.price || 0), 0), [selected]);
  const total = perPlate * (guests || 0);

  const menuPayload = useCallback(() => ({
    occasion,
    guest_count: parseInt(guests) || 0,
    dishes: selected,
    per_plate_estimate: perPlate,
  }), [occasion, guests, selected, perPlate]);

  const ensureSaved = useCallback(async () => {
    if (shareId) return shareId;
    const { data } = await api.post("/menus", menuPayload());
    setShareId(data.id);
    return data.id;
  }, [shareId, menuPayload]);

  const guardEmpty = () => {
    if (selected.length === 0) {
      toast.error("Add at least one dish first");
      return true;
    }
    return false;
  };

  const doExport = async (fmt) => {
    if (guardEmpty()) return;
    setBusy(fmt);
    try {
      const res = await api.post(`/export/${fmt}`, menuPayload(), { responseType: "blob" });
      saveAs(res.data, `mayuresh-menu.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Export failed — your menu is still saved.");
    } finally {
      setBusy(null);
    }
  };

  const doShare = async () => {
    if (guardEmpty()) return;
    setBusy("share");
    try {
      const id = await ensureSaved();
      const link = `${window.location.origin}/menu/${id}`;
      await navigator.clipboard.writeText(link);
      toast.success("Share link copied to clipboard!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const doWhatsApp = async () => {
    if (guardEmpty()) return;
    setBusy("wa");
    try {
      const id = await ensureSaved();
      const link = `${window.location.origin}/menu/${id}`;
      const wa = settings?.whatsapp || "919876543210";
      const items = selected.map((s) => `• ${s.name}`).join("\n");
      const text = `*Mayuresh Caterers — My Menu*\nOccasion: ${occasion}\nGuests: ${guests}\n\n${items}\n\nView full menu: ${link}`;
      window.open(`https://wa.me/${wa}?text=${encodeURIComponent(text)}`, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const openEnquiry = async () => {
    if (guardEmpty()) return;
    try {
      await ensureSaved();
      setShowEnquiry(true);
      setTimeout(() => document.getElementById("builder-enquiry")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error("Could not prepare menu");
    }
  };

  const filteredDishes = dishes.filter((d) => {
    if (d.category !== activeCat) return false;
    if (diet.length && !diet.every((f) => d.dietary?.includes(f))) return false;
    return true;
  });

  if (loading)
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-3 text-[#B08D4F]">
        <Loader2 className="animate-spin" size={32} />
        <p className="text-sm">Loading the kitchen… (first load may take a moment)</p>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-5 py-12" data-testid="menu-builder-page">
      <Seo
        title="Build Your Menu — Mayuresh Caterers Roha | Live Plate & Instant Estimate"
        description="Design your own catering menu online. Pick your occasion and dishes, watch them appear on a lifelike thali, get a live price estimate and share instantly."
      />

      <div className="mb-8">
        <p className="label-tag with-rule text-[#B08D4F] mb-4">Interactive Menu Builder</p>
        <h1 className="text-4xl sm:text-6xl font-medium text-[#1A1A1A] display-title">Compose your perfect <span className="italic text-gold-grad">thali</span></h1>
      </div>

      {/* occasion + guests + presets */}
      <div className="card-lux rounded-sm p-6 mb-8 grid lg:grid-cols-3 gap-6 items-end">
        <div>
          <label className="label-tag text-[#B08D4F] block mb-2">Occasion</label>
          <select value={occasion} onChange={(e) => setOccasion(e.target.value)} data-testid="occasion-select"
            className="w-full bg-white border border-[#E5DCC9] rounded-sm px-4 py-3 outline-none focus:border-[#C9A66B]">
            {["Wedding", "Corporate", "Birthday", "Private Party", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="label-tag text-[#B08D4F] mb-2 flex items-center gap-2"><Users size={13} /> Guest count</label>
          <input type="number" min={1} value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="guest-count-input"
            className="w-full bg-white border border-[#E5DCC9] rounded-sm px-4 py-3 outline-none focus:border-[#C9A66B]" />
        </div>
        <div>
          <label className="label-tag text-[#B08D4F] mb-2 flex items-center gap-2"><Sparkles size={13} /> Quick start preset</label>
          <div className="flex flex-wrap gap-2">
            {presets.filter((p) => p.name === "Mayuresh Special Thali").map((p) => (
              <button key={p.id} onClick={() => applyPreset(p)} data-testid="preset-signature"
                className="btn-gold text-xs px-3 py-2 rounded-sm flex items-center gap-1">
                <Sparkles size={12} /> {p.name}
              </button>
            ))}
            {presets.filter((p) => p.name !== "Mayuresh Special Thali").map((p) => (
              <button key={p.id} onClick={() => applyPreset(p)} data-testid={`preset-${p.occasion.toLowerCase()}`}
                className="btn-outline-gold text-xs px-3 py-2 rounded-sm">
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_460px] gap-10 items-start">
        {/* LEFT: dish selection */}
        <div>
          {/* dietary filters */}
          <div className="flex items-center gap-3 mb-5">
            <span className="label-tag text-[#B08D4F]">Dietary</span>
            {DIETARY.map((f) => {
              const on = diet.includes(f.key);
              return (
                <button key={f.key} data-testid={`diet-${f.key}`}
                  onClick={() => setDiet(on ? diet.filter((x) => x !== f.key) : [...diet, f.key])}
                  className={`text-xs px-3 py-1.5 rounded-sm border flex items-center gap-1 transition-colors ${on ? "bg-[#C9A66B] border-[#C9A66B] text-[#1A1A1A]" : "border-[#E5DCC9] text-[#5b554b]"}`}>
                  <Leaf size={12} /> {f.label}
                </button>
              );
            })}
          </div>

          {/* category tabs */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-[#E5DCC9] pb-4">
            {categories.map((c) => {
              const used = countInCat(c);
              const lim = limits[c];
              return (
                <button key={c} onClick={() => setActiveCat(c)} data-testid={`category-tab-${c.replace(/[^a-zA-Z]/g, "-")}`}
                  className={`px-4 py-2 rounded-sm text-sm transition-colors ${activeCat === c ? "bg-[#1A1A1A] text-[#C9A66B]" : "text-[#5b554b] hover:bg-[#efe9dd]"}`}>
                  {c} {lim ? <span className="text-xs opacity-70">({used}/{lim})</span> : null}
                </button>
              );
            })}
          </div>

          {/* dish grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            {filteredDishes.length === 0 && <p className="text-[#5b554b] col-span-2 py-8 text-center">No dishes match these filters.</p>}
            {filteredDishes.map((d) => {
              const on = !!selected.find((s) => s.id === d.id);
              return (
                <motion.div key={d.id} layout className={`card-lux rounded-sm overflow-hidden flex ${on ? "ring-1 ring-[#C9A66B]" : ""}`} data-testid={`dish-card-${d.id}`}>
                  <div className="w-28 shrink-0">
                    <DishImage src={d.image} alt={d.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-lg font-medium text-[#1A1A1A] leading-tight">{d.name}</h4>
                      {d.dietary?.includes("veg") && <Leaf size={14} className="text-[#6ba33f] shrink-0 mt-1" />}
                    </div>
                    <p className="text-xs text-[#5b554b] mt-1 line-clamp-2">{lang === "mr" && d.desc_mr ? d.desc_mr : d.description}</p>
                    <div className="flex items-center justify-end mt-auto pt-3">
                      <button onClick={() => toggleDish(d)} data-testid={`add-dish-${d.id}`}
                        className={`text-xs px-3 py-1.5 rounded-sm flex items-center gap-1 transition-colors ${on ? "bg-[#1A1A1A] text-[#C9A66B]" : "btn-gold"}`}>
                        {on ? <><Check size={13} /> Added</> : <><Plus size={13} /> Add</>}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: live plate + price + actions */}
        <div className="lg:sticky lg:top-24 space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="label-tag text-[#B08D4F]">{lang === "mr" ? "तुमचं ताट" : "Your Thali"}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex border border-[#E5DCC9] rounded-sm overflow-hidden" data-testid="view-toggle">
                <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-[#1A1A1A] text-[#C9A66B]" : "text-[#5b554b] hover:bg-[#efe9dd]"}`}><List size={13} /> {lang === "mr" ? "यादी" : "List"}</button>
                <button onClick={() => setView("plate")} data-testid="view-plate" className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors ${view === "plate" ? "bg-[#1A1A1A] text-[#C9A66B]" : "text-[#5b554b] hover:bg-[#efe9dd]"}`}><UtensilsCrossed size={13} /> {lang === "mr" ? "ताट" : "Plate"}</button>
              </div>
              <button onClick={clearAll} disabled={selected.length === 0} data-testid="clear-plate-btn" className="text-xs px-3 py-1.5 rounded-sm border border-[#E5DCC9] text-[#b0553f] hover:bg-[#f4e9e5] disabled:opacity-40 flex items-center gap-1.5"><Trash2 size={13} /> {lang === "mr" ? "साफ करा" : "Clear"}</button>
            </div>
          </div>
          {view === "plate" && (
            <div className="flex items-center gap-2" data-testid="center-toggle">
              <span className="label-tag text-[#B08D4F] text-[0.55rem]">{lang === "mr" ? "मध्यभागी" : "Centre"}</span>
              <button onClick={() => setCenter("rice")} data-testid="center-rice" className={`text-xs px-3 py-1 rounded-sm border transition-colors ${center === "rice" ? "bg-[#C9A66B] border-[#C9A66B] text-[#1A1A1A]" : "border-[#E5DCC9] text-[#5b554b]"}`}>{lang === "mr" ? "भात" : "Rice"}</button>
              <button onClick={() => setCenter("bhakri")} data-testid="center-bhakri" className={`text-xs px-3 py-1 rounded-sm border transition-colors ${center === "bhakri" ? "bg-[#C9A66B] border-[#C9A66B] text-[#1A1A1A]" : "border-[#E5DCC9] text-[#5b554b]"}`}>{lang === "mr" ? "भाकरी" : "Bhakri"}</button>
            </div>
          )}
          <div className="card-lux rounded-sm p-6">
            <PlateComposer dishes={selected} onRemove={removeDish} view={view} lang={lang} center={center} />
          </div>

          <div className="bg-[#1A1A1A] rounded-sm p-6 text-white" data-testid="menu-summary">
            <div className="flex justify-between items-center">
              <span className="text-white/60 text-sm">Occasion</span>
              <span className="serif text-2xl text-[#C9A66B]">{occasion}</span>
            </div>
            <div className="gold-line my-4" />
            <div className="flex justify-between items-center">
              <span className="text-white/60 text-sm">Dishes · Guests</span>
              <span className="serif text-2xl text-white">{selected.length} · {guests}</span>
            </div>
            <p className="text-white/40 text-xs mt-3">Final quote confirmed by Mayuresh Caterers.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => doExport("pdf")} disabled={busy} className="btn-outline-gold px-4 py-3 rounded-sm text-sm flex items-center justify-center gap-2" data-testid="export-pdf-btn">
              {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} PDF
            </button>
            <button onClick={() => doExport("docx")} disabled={busy} className="btn-outline-gold px-4 py-3 rounded-sm text-sm flex items-center justify-center gap-2" data-testid="export-docx-btn">
              {busy === "docx" ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} Word
            </button>
            <button onClick={doShare} disabled={busy} className="btn-outline-gold px-4 py-3 rounded-sm text-sm flex items-center justify-center gap-2" data-testid="share-link-btn">
              {busy === "share" ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />} Copy Link
            </button>
            <button onClick={doWhatsApp} disabled={busy} className="bg-[#25D366] text-white px-4 py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:brightness-95 transition-all" data-testid="whatsapp-share-btn">
              {busy === "wa" ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />} WhatsApp
            </button>
          </div>

          <button onClick={openEnquiry} className="btn-gold w-full px-6 py-4 rounded-sm flex items-center justify-center gap-2 text-sm" data-testid="send-to-caterer-btn">
            <Send size={18} /> Send this menu & get a quote
          </button>

          {shareId && (
            <div className="text-xs text-center text-[#B08D4F] flex items-center justify-center gap-1" data-testid="share-confirmation">
              <Share2 size={12} /> Menu saved — link ready to share
            </div>
          )}
        </div>
      </div>

      {showEnquiry && (
        <div id="builder-enquiry" className="card-lux rounded-sm p-8 sm:p-10 mt-14 max-w-2xl">
          <h2 className="text-3xl font-semibold text-[#1A1A1A] mb-2">Send your menu to Mayuresh Caterers</h2>
          <p className="text-[#5b554b] mb-6 text-sm">Your built menu is attached automatically. We'll confirm your final quote.</p>
          <EnquiryForm menuId={shareId} defaults={{ occasion, guest_count: guests }} onSuccess={() => setShowEnquiry(false)} />
        </div>
      )}
    </div>
  );
}
